import React from 'react';
import { Box } from '@chakra-ui/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhyUsHero from '../components/whyus/WhyUsHero';
import WhyUsFounderStory from '../components/whyus/WhyUsFounderStory';
import WhyUsDifferentiators from '../components/whyus/WhyUsDifferentiators';
import WhyUsJourney from '../components/whyus/WhyUsJourney';
import WhyUsTeam from '../components/whyus/WhyUsTeam';
import WhyUsStats from '../components/whyus/WhyUsStats';
import WhyUsComparison from '../components/whyus/WhyUsComparison';
import WhyUsCTA from '../components/whyus/WhyUsCTA';
import {
  FaRocket, FaUsers,
  FaGraduationCap, FaVideo, FaLifeRing, FaInfinity, FaMobile,
  FaChartLine, FaStar, FaBriefcase
} from 'react-icons/fa';
import { useAppData } from "../store/dataStore";
import { Helmet } from 'react-helmet-async';
import { useAnalytics } from '../hooks/useAnalytics';  // ✅ Added import

const WhyUsPage = () => {

  const { appData } = useAppData();
  const { trackEvent } = useAnalytics();  // ✅ Initialize analytics
  const { studentsCount } = appData;

  const whyUsData = {
    hero: {
      title: `Why ${studentsCount}+ Students Trust Us`,
      subtitle: "We're not just teachers—we're practitioners who've built real products and shaped careers."
    },

    founderStory: {
      title: "We've Walked The Path",
      description1: "Our journey began with building an AI-powered algo trading platform. That real-world experience shapes every lesson we teach.",
      description2: "We understand the challenges because we've faced them ourselves.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800"
    },

    differentiators: {
      title: "What Sets Us Apart",
      subtitle: "Eight reasons why students choose us over traditional courses.",
      items: [
        { icon: FaRocket, title: 'Real-World Experience', desc: 'Built by GenAI practitioners with proven industry success.' },
        { icon: FaChartLine, title: 'Industry Veterans', desc: '100+ years combined expertise in AI and development.' },
        { icon: FaGraduationCap, title: 'Proven Curriculum', desc: `${studentsCount}+ graduates successfully placed in top companies.` },
        { icon: FaVideo, title: 'Live, Not Recorded', desc: 'Interactive sessions with real-time doubt resolution.' },
        { icon: FaUsers, title: 'Small Batch Sizes', desc: 'Personalized attention in every Batch.' },
        { icon: FaLifeRing, title: 'Career Support', desc: 'End-to-end placement assistance included.' },
        { icon: FaInfinity, title: '1 year Access', desc: '1 year access to materials and forever access to community.' },
        { icon: FaMobile, title: 'Learning Portal', desc: 'Learn on-the-go with our dedicated platform.' },
      ]
    },

    journey: {
      title: "Our Journey",
      subtitle: "Milestones that shaped our mission.",
      milestones: [
        { year: '2021', title: 'Trading platform Launch', desc: 'Started our AI Algo-trading platform.' },
        { year: '2022', title: 'First Training Batch', desc: 'Launched AI/ML course with 5 students.' },
        { year: '2024', title: `100+ Graduates`, desc: 'Reached milestone of trained professionals.' },
        { year: '2025', title: 'Enterprise Expansion', desc: 'Working on partnership with leading companies.' },
      ]
    },

    team: {
      title: "Meet The Mentors",
      subtitle: "Learn from practitioners who've built real AI solutions.",
      members: [
        { name: 'Venkat Kaluvai', role: 'AI/ML Expert', image: '', expertise: '15 years in Software Engg' },
        { name: 'Roopesh Tayaloor', role: 'GenAI Specialist', image: '', expertise: '30+ years in IT, 7 years in AI/ML' },
        { name: 'Yatish', role: 'Career Advisor', image: '', expertise: 'Placed Hundreds of candidates' },
      ]
    },

    stats: {
      items: [
        { icon: FaUsers, value: `${studentsCount}+`, label: 'Students Trained' },
        { icon: FaStar, value: '4.8/5', label: 'Average Rating' },
        { icon: FaGraduationCap, value: '95%', label: 'Completion Rate' },
        { icon: FaBriefcase, value: '85%', label: 'Placement Rate' },
      ]
    },

    comparison: {
      title: "Why We Stand Out",
      subtitle: "The complete learning experience others miss",
      columns: [
        {
          category: 'Our Program',
          features: [
            { name: 'Post-Class Assessments', available: true },
            { name: 'Interactive Live Classes', available: true },
            { name: 'Real Project Support', available: true },
            { name: 'Career & Interview Prep', available: true },
            { name: 'Industry Expert Mentors', available: true },
            { name: 'Step-by-Step Code Demos', available: true },
            { name: 'Small Batch Learning', available: true },
            { name: 'Lifetime Content Access', available: true },
          ],
        },
        {
          category: 'Traditional Courses',
          features: [
            { name: '', available: true },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
          ],
        },
        {
          category: 'Self-Learning',
          features: [
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: false },
            { name: '', available: true },
          ],
        },
      ]
    },

    cta: {
      title: "Experience The Difference",
      subtitle: `Join  ${studentsCount}+ students who've transformed their careers with our proven approach.`,
      buttonText: "Book a Demo",
      href: "/webinar"
    }
  };

  return (
    <>
      <Helmet>
        <title>Why Ojasa Mirai | Real-World Mentorship & Proven AI Career Results</title>
        <meta
          name="description"
          content="Discover why 300+ students trust Ojasa Mirai. Learn from AI practitioners, build real projects, and get guided career support. We’ve walked the path — now we’ll guide you on yours."
        />
      </Helmet>

      <Box>
        <Navbar />
        <WhyUsHero data={whyUsData.hero} />
        <WhyUsFounderStory data={whyUsData.founderStory} />
        <WhyUsDifferentiators data={whyUsData.differentiators} />
        <WhyUsJourney data={whyUsData.journey} />
        <WhyUsTeam data={whyUsData.team} />
        <WhyUsStats data={whyUsData.stats} />
        <WhyUsComparison data={whyUsData.comparison} />

        {/* ✅ Inject CTA with analytics */}
        <WhyUsCTA
          data={whyUsData.cta}
          onClick={() =>
            trackEvent('book_demo_click', {
              page: 'why_us',
              cta_label: 'book_free_demo',
            })
          }
        />

        <Footer />
      </Box>
    </>
  );
};

export default WhyUsPage;
